import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import useIsMobile from '../../hooks/useIsMobile';
import ProgramTimeline from '../../components/ProgramTimeline/ProgramTimeline';
import ProgramTimelineMobile from '../../components/ProgramTimeline/ProgramTimelineMobile';
import CourseTimelineOverview from '../../components/ProgramTimeline/CourseTimelineOverview';
import FAQ from '../../components/FAQ/FAQ';
import PackagePriceCard from '../../components/PackagePriceCard/PackagePriceCard';
import './ProgramDetails.css';
import BackTo from '../../components/ui/BackTo/BackTo';
import { Programs as ProgramsEN } from '../../data/programs';
import { Programs as ProgramsRU } from '../../data/ruprograms';
import { programFAQ, programFAQru } from './../../data/faqMain';
import VideoContainer from '../../components/VideoContainer/VideoContainer';
import MasterSection from '../../components/MasterSection/MasterSection';

const ProgramDetails: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { programId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // ✅ Select the correct programs data based on the current language
  const Programs = i18n.language === 'ru' ? ProgramsRU : ProgramsEN;
  const faqData = i18n.language === 'ru' ? programFAQru : programFAQ; // ✅ Use correct FAQ dataset
  const program = Programs.find((p) => p.id === programId);
  if (!program) {
    return <h1>{t("program_not_found")}</h1>;
  }

  const handleJoinCourse = () => {
    window.location.href = program.stripeLink;
  };

  const goToMasterPage = () => {
    if (program.masterId) {
      navigate(`/masters/${program.masterId}`);
    }
  };

  const events = program.events.map((event) => ({
    date: event.date,
    dateOfEvent: event.dateOfEvent, 
    time: event.time || 'TBD',
    title: event.name,
    description: event.shortDescription || t("no_description"),
    location: event.location,
    locationLink: event.locationLink,
    status: t("upcoming"),
    programId: program.id,
    eventId: event.id,
    stripeLink: event.stripeLink || '',
    price: event.priceOfEvent || '0',
  }));
  const today = new Date();
  const programStartDate = new Date(program.startDate);
  
  return (
    <div style={{ marginTop: '80px' }}>
      <BackTo text={t("programs")} />

      <CourseTimelineOverview
        date={program.dateRange}
        title={program.title}
        description={program.description}
        image={program.image}
        stats={{
          lessons: program.noOfEvents,
          lectures: program.noOfEvents,
          hours: program.noOfEvents * 2,
          certificate: true,
        }}
        buttonText={t("join_program")}
        onButtonClick={handleJoinCourse}
        programPrice={program.price}
        mastersName={program.Masters} whatsIncluded={program.whatsIncluded} 
        startDate={program.startDate}       />

      {/* Conditionally Render Timeline Based on Screen Size */}
      {isMobile ? <ProgramTimelineMobile events={events} /> : <ProgramTimeline events={events} />}

      {program.videoLink && (
        <VideoContainer videoThumbnail={program.image} videoUrl={program.videoLink} />
      )}

      <MasterSection
        backgroundImage={program.programMasterImage}
        title={program.Masters}
        subtitle={t("expert_in_field")}
        actionText={t("learn_more")}
        onActionClick={goToMasterPage}
      />

     {/* Hide Payment Section If Start Date is in the Past */}
    {programStartDate >= today && (
      <PackagePriceCard
        title={t("buy_full_access")}
        description={t("full_program_access")}
        price={program.price}
        buttonText={t("join_program")}
        subtext={t("buy_lessons_separately")}
        onButtonClick={handleJoinCourse}
      />
    )}

      <FAQ data={faqData} />
    </div>
  );
};

export default ProgramDetails;
